/**
 * Phone-number masking for agent screens. The rule comes from the agent's
 * campaign (/User/getCampaignMask, set on the AddCampaign page) as
 * { mode, digits }:
 *   NONE       full number
 *   FIRST      hide the first N digits
 *   LAST       hide the last N digits
 *   KEEP_LAST  hide everything except the last N digits
 *   ALL        hide every digit
 * Only digits are replaced (with 'X'); '+', spaces and dashes stay put.
 * The backend returns NONE for non-agent roles.
 */
import { useEffect, useState } from 'react';

export const maskNumber = (number, rule) => {
  if (number === null || number === undefined) return number;
  const text = String(number);
  const mode = rule && rule.mode ? String(rule.mode).toUpperCase() : 'NONE';
  if (mode === 'NONE') return text;
  const total = (text.match(/\d/g) || []).length;
  const n = Math.max(0, Number(rule.digits) || 0);
  let from = 0;
  let to = 0; // hide digit positions [from, to)
  if (mode === 'ALL') { from = 0; to = total; }
  else if (mode === 'FIRST') { from = 0; to = Math.min(n, total); }
  else if (mode === 'LAST') { from = Math.max(0, total - n); to = total; }
  else if (mode === 'KEEP_LAST') { from = 0; to = Math.max(0, total - n); }
  else return text;
  let i = 0;
  return text.replace(/\d/g, (d) => {
    const hide = i >= from && i < to;
    i += 1;
    return hide ? 'X' : d;
  });
};

/** Human-readable summary, e.g. "Hide first 6 digits". */
export const describeMask = (rule) => {
  const mode = rule && rule.mode ? String(rule.mode).toUpperCase() : 'NONE';
  const n = Number(rule && rule.digits) || 0;
  if (mode === 'FIRST') return `Hide first ${n} digits`;
  if (mode === 'LAST') return `Hide last ${n} digits`;
  if (mode === 'KEEP_LAST') return `Show only last ${n} digits`;
  if (mode === 'ALL') return 'Hide all digits';
  return 'No masking';
};

// Fetched once per page load and shared by every screen.
// Until it arrives — or if it fails — numbers stay hidden.
const SAFE_DEFAULT = { mode: 'ALL', digits: 0 };
let cached = null;

const loadMask = () => {
  if (!cached) {
    cached = fetch(process.env.REACT_APP_IP + '/User/getCampaignMask', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((json) => (json && json.mode ? json : SAFE_DEFAULT))
      .catch(() => { cached = null; return SAFE_DEFAULT; });
  }
  return cached;
};

const listeners = new Set();

/** Agent switched campaign: fetch the rule again and update every screen using it. */
export const refreshCampaignMask = () => {
  cached = null;
  loadMask().then((r) => listeners.forEach((fn) => fn(r)));
};

export const useCampaignMask = () => {
  const [rule, setRule] = useState(SAFE_DEFAULT);
  useEffect(() => {
    let alive = true;
    const set = (r) => { if (alive) setRule(r); };
    listeners.add(set);
    loadMask().then(set);
    return () => { alive = false; listeners.delete(set); };
  }, []);
  return rule;
};
